"use client"

import { DownOutlined } from "@ant-design/icons"
import { ComponentConfig } from "@measured/puck"
import { message } from "antd"
import { useEffect, useState } from "react"
import { MenuApiResponse, MenuItem } from "~/types/types"
import { t } from "../../../../../../languages/i18n"
import menuService from "../../../../../menu/service/menuService"
import { DefaultImage, configs } from "../../../../external-components"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface NavigationProps extends CommonStylesProps {
  url: string
  menu: string
  slogan?: string
  logoStyles?: {
    width: string
    height: string
    padding: string
    margin: string
    objectFit: "cover" | "contain" | "fill" | "none" | "scale-down"
  }
  sloganStyles?: {
    fontSize: string
    fontWeight: string
    color: string
    margin: string
    padding: string
  }
  type: "fluid" | "custom" | "boxed"
}

interface MenuItemWithSubItems extends MenuItem {
  subItems?: MenuItem[]
}

export const RenderConfig: ComponentConfig<NavigationProps> = {
  render: ({ url, menu, slogan, logoStyles = {}, sloganStyles = {}, className, type, styles, responsiveType }) => {
    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const [organizedMenuItems, setOrganizedMenuItems] = useState<MenuItemWithSubItems[]>([])

    const organizeMenuItems = (items: MenuItem[]): MenuItemWithSubItems[] => {
      const sortedItems = [...items].sort((a, b) => a.order - b.order)

      const parentItems = sortedItems.filter(item => item.parent === 0)

      let currentParentIndex = -1
      const childrenGroups: { [parentId: number]: MenuItem[] } = {}

      sortedItems.forEach(item => {
        if (item.parent === 0) {
          currentParentIndex = item.id
        } else if (item.parent === 1 && currentParentIndex !== -1) {
          if (!childrenGroups[currentParentIndex]) {
            childrenGroups[currentParentIndex] = []
          }
          childrenGroups[currentParentIndex].push(item)
        }
      })

      return parentItems.map(parent => ({
        ...parent,
        subItems: childrenGroups[parent.id] || [],
      }))
    }

    const fetchMenuItems = async () => {
      if (!menu) return

      try {
        const response = await menuService.fetchAllMenuPreference()
        const data = response.data

        if (data.length > 0) {
          const selectedMenuPreference = data.find((item: MenuApiResponse) => item.attributes.name === menu)

          if (selectedMenuPreference) {
            const organized = organizeMenuItems(selectedMenuPreference.attributes.items || [])
            setOrganizedMenuItems(organized)
          } else {
            setOrganizedMenuItems([])
          }
        }
      } catch (error) {
        message.error(t("Error fetching menu preferences. Please try again."))
      }
    }

    useEffect(() => {
      fetchMenuItems()
    }, [menu])
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        <div id={id}>
          <div className="w-full">
            <div className="w-full flex flex-row justify-between items-center p-4">
              <img
                src={url ? `${configs.API_URL}${url}` : DefaultImage}
                style={{
                  width: logoStyles.width || "200px",
                  height: logoStyles.height || "73px",
                  objectFit: logoStyles.objectFit || "cover",
                  padding: logoStyles.padding || "0px",
                  margin: logoStyles.margin || "0px",
                }}
                className="cursor-pointer"
                alt="Logo"
              />

              <div className="flex">
                <ul className="flex flex-row gap-3 justify-end">
                  {organizedMenuItems.map(item => (
                    <li
                      key={item.id}
                      className="relative text-gray-700 p-2 hover:text-teal-600 cursor-pointer duration-200 transition-colors group pb-2"
                    >
                      <div className="flex flex-row gap-2 items-center justify-center">
                        <a href={item.slug ? `/${item.slug}` : "#"} className="font-semibold uppercase">
                          {item.name}
                        </a>
                        {item.subItems?.length !== 0 ? <DownOutlined style={{ fontSize: "8px" }} /> : ""}
                      </div>

                      {item.subItems && item.subItems.length > 0 && (
                        <ul className="absolute hidden border group-hover:block bg-white shadow-lg rounded-md mt-2 min-w-[200px] z-50">
                          {item.subItems.map(subItem => (
                            <a
                              key={subItem.id}
                              href={subItem.slug ? `/${subItem.slug}` : "#"}
                              className="block px-4 py-2 text-gray-700 duration-200 transition-colors hover:text-teal-600 text-nowrap"
                            >
                              {subItem.name}
                            </a>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {slogan && (
                <div
                  style={{
                    margin: sloganStyles.margin || "2rem",
                    padding: sloganStyles.padding || "0px",
                    fontSize: sloganStyles.fontSize || "16px",
                    fontWeight: sloganStyles.fontWeight || "300",
                    color: sloganStyles.color || "#4B5563",
                  }}
                >
                  {slogan}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    )
  },
}
